import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormLabel,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
  IconButton,
} from '@mui/joy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BlockIcon from '@mui/icons-material/Block';
import type {
  BatchAttendance,
  BatchAttendanceRecord,
  CongregationBatch,
  CongregationMember,
} from '../../types/congregation';
import {
  listAllBatchAttendance,
  listCongregationBatches,
  fetchAllAdminMembers,
  getBatchAttendanceById,
  getMemberAttendanceHistory,
} from '../../api';

export default function AdminBatchAttendance() {
  const [attendances, setAttendances] = useState<BatchAttendance[]>([]);
  const [batches, setBatches] = useState<CongregationBatch[]>([]);
  const [mentors, setMentors] = useState<CongregationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [detailsCache, setDetailsCache] = useState<Record<number, BatchAttendanceRecord[]>>({});
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [selectedMemberName, setSelectedMemberName] = useState<string | null>(null);
  const [memberHistoryLoading, setMemberHistoryLoading] = useState(false);
  const [memberHistory, setMemberHistory] = useState<BatchAttendanceRecord[]>([]);

  // Filters
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(3, 'month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedMentorId, setSelectedMentorId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load metadata (batches and mentors)
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        setLoadingMeta(true);
        const [batchesData, membersData] = await Promise.all([
          listCongregationBatches(),
          fetchAllAdminMembers({ limit: 500 }), // Get all members
        ]);

        setBatches(batchesData.batches || []);
        
        // Filter mentors (members with roles)
        const mentorMembers = (membersData.results || [])
          .map((result) => result.member)
          .filter((m): m is CongregationMember => m !== undefined && m !== null);
        setMentors(mentorMembers);
      } catch (error) {
        console.error('Failed to load metadata:', error);
      } finally {
        setLoadingMeta(false);
      }
    };

    loadMetadata();
  }, []);

  // Load attendance data
  const loadAttendances = async () => {
    try {
      setLoading(true);
      const start = startDate.format('YYYY-MM-DD');
      const end = endDate.format('YYYY-MM-DD');
      const response = await listAllBatchAttendance(
        start,
        end,
        selectedBatchId || undefined,
        selectedMentorId || undefined
      );
      setAttendances(response.attendances || []);
    } catch (error) {
      console.error('Failed to load attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle expand for attendance details
  const toggleExpanded = async (attendanceId: number, batchId: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(attendanceId)) {
      newExpanded.delete(attendanceId);
    } else {
      // Fetch details if not cached
      if (!detailsCache[attendanceId]) {
        try {
          const response = await getBatchAttendanceById(batchId, attendanceId);
          setDetailsCache((prev) => ({
            ...prev,
            [attendanceId]: response.records || [],
          }));
        } catch (error) {
          console.error('Failed to load attendance details');
          return;
        }
      }
      newExpanded.add(attendanceId);
    }
    setExpandedIds(newExpanded);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PRESENT':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'ABSENT':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      case 'LATE':
        return <AccessTimeIcon sx={{ fontSize: 16 }} />;
      case 'EXCUSED':
        return <BlockIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  // View member attendance history
  const handleViewMemberHistory = async (memberId: number, memberName: string) => {
    setSelectedMemberId(memberId);
    setSelectedMemberName(memberName);
    setMemberHistoryLoading(true);
    try {
      const response = await getMemberAttendanceHistory(
        memberId,
        dayjs().subtract(6, 'month').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD')
      );
      setMemberHistory(response.records || []);
    } catch (error) {
      console.error('Failed to load member attendance history');
      setSelectedMemberId(null);
      setSelectedMemberName(null);
    } finally {
      setMemberHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!loadingMeta) {
      loadAttendances();
    }
  }, [startDate, endDate, selectedBatchId, selectedMentorId, loadingMeta]);

  // Filtered attendances based on search
  const filteredAttendances = useMemo(() => {
    if (!searchQuery) return attendances;
    const query = searchQuery.toLowerCase();
    return attendances.filter(
      (a) =>
        a.batchName?.toLowerCase().includes(query) ||
        a.sessionTopic?.toLowerCase().includes(query) ||
        a.createdByName?.toLowerCase().includes(query)
    );
  }, [attendances, searchQuery]);

  // Statistics
  const stats = useMemo(() => {
    const totalSessions = filteredAttendances.length;
    const totalPresent = filteredAttendances.reduce((sum, a) => sum + (a.totalPresent || 0), 0);
    const totalAbsent = filteredAttendances.reduce((sum, a) => sum + (a.totalAbsent || 0), 0);
    const totalLate = filteredAttendances.reduce((sum, a) => sum + (a.totalLate || 0), 0);
    const totalExcused = filteredAttendances.reduce((sum, a) => sum + (a.totalExcused || 0), 0);
    const totalRecords = totalPresent + totalAbsent + totalLate + totalExcused;
    const avgAttendanceRate = totalRecords > 0 ? (totalPresent / totalRecords) * 100 : 0;

    return {
      totalSessions,
      totalPresent,
      totalAbsent,
      totalLate,
      totalExcused,
      totalRecords,
      avgAttendanceRate,
    };
  }, [filteredAttendances]);

  // Export to CSV
  const handleExport = () => {
    const headers = ['Date', 'Batch', 'Topic', 'Mentor', 'Present', 'Absent', 'Late', 'Excused', 'Total'];
    const rows = filteredAttendances.map((a) => [
      dayjs(a.sessionDate).format('YYYY-MM-DD'),
      a.batchName || '',
      a.sessionTopic || '',
      a.createdByName || '',
      a.totalPresent || 0,
      a.totalAbsent || 0,
      a.totalLate || 0,
      a.totalExcused || 0,
      (a.totalPresent || 0) + (a.totalAbsent || 0) + (a.totalLate || 0) + (a.totalExcused || 0),
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-attendance-${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography level="h2">Batch Attendance</Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              View and analyze attendance across all batches and mentors
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<RefreshIcon />}
              onClick={loadAttendances}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              variant="soft"
              color="primary"
              startDecorator={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={filteredAttendances.length === 0}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>

        {/* Statistics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Card variant="outlined">
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Total Sessions
            </Typography>
            <Typography level="h3">{stats.totalSessions}</Typography>
          </Card>
          <Card variant="outlined">
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Avg. Attendance Rate
            </Typography>
            <Typography level="h3">{stats.avgAttendanceRate.toFixed(1)}%</Typography>
          </Card>
          <Card variant="outlined">
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Total Present
            </Typography>
            <Typography level="h3" sx={{ color: 'success.500' }}>
              {stats.totalPresent}
            </Typography>
          </Card>
          <Card variant="outlined">
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Total Absent
            </Typography>
            <Typography level="h3" sx={{ color: 'danger.500' }}>
              {stats.totalAbsent}
            </Typography>
          </Card>
        </Box>

        {/* Filters */}
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography level="title-md" startDecorator={<FilterListIcon />}>
              Filters
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              {/* Date Range */}
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <DatePicker
                  value={startDate}
                  onChange={(date) => date && setStartDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                          color: 'var(--joy-palette-text-primary)',
                          '& fieldset': {
                            borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'var(--joy-palette-primary-outlinedBorder)',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'var(--joy-palette-text-primary)',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'var(--joy-palette-text-tertiary)',
                          opacity: 1,
                        },
                        '& .MuiIconButton-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& .MuiFormLabel-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& label': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                      },
                    },
                    inputAdornment: {
                      sx: {
                        color: 'var(--joy-palette-text-primary)',
                      },
                    },
                  }}
                />
              </FormControl>

              <FormControl>
                <FormLabel>End Date</FormLabel>
                <DatePicker
                  value={endDate}
                  onChange={(date) => date && setEndDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                          color: 'var(--joy-palette-text-primary)',
                          '& fieldset': {
                            borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'var(--joy-palette-primary-outlinedBorder)',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'var(--joy-palette-text-primary)',
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'var(--joy-palette-text-tertiary)',
                          opacity: 1,
                        },
                        '& .MuiIconButton-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& .MuiFormLabel-root': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                        '& label': {
                          color: 'var(--joy-palette-text-primary)',
                        },
                      },
                    },
                    inputAdornment: {
                      sx: {
                        color: 'var(--joy-palette-text-primary)',
                      },
                    },
                  }}
                />
              </FormControl>

              {/* Batch Filter */}
              <FormControl>
                <FormLabel>Batch</FormLabel>
                <Select
                  size="sm"
                  value={selectedBatchId}
                  onChange={(_, value) => setSelectedBatchId(value)}
                  placeholder="All Batches"
                >
                  <Option value={null}>All Batches</Option>
                  {batches.map((batch) => (
                    <Option key={batch.id} value={batch.id}>
                      {batch.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>

              {/* Mentor Filter */}
              <FormControl>
                <FormLabel>Mentor</FormLabel>
                <Select
                  size="sm"
                  value={selectedMentorId}
                  onChange={(_, value) => setSelectedMentorId(value)}
                  placeholder="All Caretakers"
                >
                  <Option value={null}>All Caretakers</Option>
                  {mentors.map((mentor) => (
                    <Option key={mentor.id} value={mentor.id}>
                      {mentor.displayName}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Search */}
            <Input
              startDecorator={<SearchIcon />}
              placeholder="Search by batch name, topic, or mentor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Stack>
        </Card>

        {/* Attendance Table */}
        <Card variant="outlined">
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="title-lg">Attendance Records</Typography>
              <Chip variant="soft" color="neutral">
                {filteredAttendances.length} record{filteredAttendances.length !== 1 ? 's' : ''}
              </Chip>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>
              <LinearProgress />
              <Typography level="body-sm" sx={{ mt: 1, textAlign: 'center' }}>
                Loading attendance records...
              </Typography>
            </Box>
          ) : filteredAttendances.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
                No attendance records found for the selected filters
              </Typography>
            </Box>
          ) : (
            <Sheet
              sx={{
                overflow: 'auto',
                maxHeight: 600,
                width: '100%',
              }}
            >
              <Table
                stickyHeader
                sx={{
                  width: '100%',
                  '& tbody tr': {
                    '& td': {
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }
                  }
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}></th>
                    <th style={{ width: '10%' }}>Date</th>
                    <th style={{ width: '18%' }}>Batch</th>
                    <th style={{ width: '22%' }}>Topic</th>
                    <th style={{ width: '15%' }}>Mentor</th>
                    <th style={{ width: '9%' }}>Present</th>
                    <th style={{ width: '9%' }}>Absent</th>
                    <th style={{ width: '8%' }}>Late</th>
                    <th style={{ width: '9%' }}>Excused</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendances.map((attendance) => {
                    const total =
                      (attendance.totalPresent || 0) +
                      (attendance.totalAbsent || 0) +
                      (attendance.totalLate || 0) +
                      (attendance.totalExcused || 0);
                    const presentRate = total > 0 ? ((attendance.totalPresent || 0) / total) * 100 : 0;

                    return (
                      <React.Fragment key={attendance.attendanceId}>
                        <tr>
                          <td>
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => toggleExpanded(attendance.attendanceId, attendance.batchId)}
                              sx={{
                                transform: expandedIds.has(attendance.attendanceId)
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </td>
                          <td>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              {dayjs(attendance.sessionDate).format('MMM DD')}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                              {dayjs(attendance.sessionDate).format('YYYY')}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              level="body-sm"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {attendance.batchName || '-'}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              level="body-sm"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {attendance.sessionTopic || '-'}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              level="body-sm"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {attendance.createdByName || '-'}
                            </Typography>
                          </td>
                          <td>
                            <Chip size="sm" color="success" variant="soft">
                              {attendance.totalPresent || 0}
                            </Chip>
                          </td>
                          <td>
                            <Chip size="sm" color="danger" variant="soft">
                              {attendance.totalAbsent || 0}
                            </Chip>
                          </td>
                          <td>
                            <Chip size="sm" color="warning" variant="soft">
                              {attendance.totalLate || 0}
                            </Chip>
                          </td>
                          <td>
                            <Chip size="sm" color="neutral" variant="soft">
                              {attendance.totalExcused || 0}
                            </Chip>
                          </td>
                        </tr>
                        {expandedIds.has(attendance.attendanceId) && (
                          <tr>
                            <td colSpan={9} style={{ padding: 0 }}>
                              <Box sx={{ p: 2, bgcolor: 'background.level1' }}>
                                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1.5 }}>
                                  Attendance Details
                                </Typography>
                                <Sheet sx={{ overflow: 'auto', maxHeight: 300 }}>
                                  <Table size="sm" sx={{ '--Table-cell-paddingY': '8px' }}>
                                    <thead>
                                      <tr>
                                        <th>Member</th>
                                        <th>Status</th>
                                        <th>Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(detailsCache[attendance.attendanceId] || []).map(
                                        (record) => (
                                          <tr key={record.recordId}>
                                            <td>
                                              <Button
                                                variant="plain"
                                                size="sm"
                                                onClick={() =>
                                                  handleViewMemberHistory(record.memberId, record.memberName || 'Unknown')
                                                }
                                                sx={{ textAlign: 'left', justifyContent: 'flex-start' }}
                                              >
                                                {record.memberName || 'Unknown'}
                                              </Button>
                                            </td>
                                            <td>
                                              <Stack direction="row" spacing={0.5} alignItems="center">
                                                {getStatusIcon(record.status)}
                                                <Chip size="sm" variant="soft">
                                                  {record.status}
                                                </Chip>
                                              </Stack>
                                            </td>
                                            <td>
                                              <Typography level="body-sm">
                                                {record.notes || '-'}
                                              </Typography>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </Table>
                                </Sheet>
                              </Box>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </Sheet>
          )}
        </Card>

        {/* Member History Modal */}
        {selectedMemberId && (
          <Card variant="outlined" sx={{ mt: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography level="title-lg">Attendance History - {selectedMemberName}</Typography>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                  setSelectedMemberId(null);
                  setSelectedMemberName(null);
                  setMemberHistory([]);
                }}
              >
                Close
              </Button>
            </Box>

            {memberHistoryLoading ? (
              <Box sx={{ p: 3 }}>
                <LinearProgress />
              </Box>
            ) : memberHistory.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
                  No attendance records found in the last 6 months
                </Typography>
              </Box>
            ) : (
              <Sheet sx={{ overflow: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>Date</th>
                      <th style={{ width: '25%' }}>Batch</th>
                      <th style={{ width: '20%' }}>Topic</th>
                      <th style={{ width: '15%' }}>Status</th>
                      <th style={{ width: '20%' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberHistory.map((record) => (
                      <tr key={record.recordId}>
                        <td>
                          <Typography level="body-sm">
                            {record.markedAt ? dayjs(record.markedAt).format('MMM DD, YYYY') : '-'}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-sm" noWrap>
                            {record.batchName || '-'}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-sm" noWrap>
                            {record.sessionTopic || '-'}
                          </Typography>
                        </td>
                        <td>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {getStatusIcon(record.status)}
                            <Chip size="sm" variant="soft">
                              {record.status}
                            </Chip>
                          </Stack>
                        </td>
                        <td>
                          <Typography level="body-sm">
                            {record.notes || '-'}
                          </Typography>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            )}
          </Card>
        )}
      </Stack>
    </LocalizationProvider>
  );
}
